import { InputResultType } from 'app/shared/components/InputTypes'
import { comUtil } from 'app/shared/utils'
import _ from 'lodash'
import React from 'react'

interface UseValidationProps {
    /**
     * 검증 타입
     * 'Number' | 숫자만
     * 'String' | 문자만
     * 'Email'  | 이메일
     * 'TEL'    | 전화번호 / 휴대폰번호
     * 'Korean' | 한글
     * 'Custom' | 커스텀
     */
    type: 'Number' | 'String' | 'Email' | 'TEL' | 'Korean' | 'Custom'
    /** 검증문자열 */
    value: string
    /** validate 실패 시 문구 */
    message?: string
    /** 문자열길이 - 최소 */
    minLength?: number
    minLengthMsg?: string
    /** 문자열길이 - 최대 */
    maxLength?: number
    maxLengthMsg?: string
    /** 필수여부 */
    required?: boolean
    requiredMsg?: string

    /**
     * 커스텀 밸리데이션 함수
     */
    customFn?: (value: string) => string
}

interface ValidationErrorInfo {
    isError: boolean
    result: InputResultType
    resultMsg: string
}

export const NUMBER_REGEXP = /^[0-9]*$/
export const STRING_REGEXP = /^[aA-zZ가-힣]*$/
export const KOREAN_REGEXP = /^([\u3130-\u318F\uAC00-\uD7AF]+)$/
export const TEL_REGEXP =
    /^(0(2|3[1-3]|4[1-4]|5[1-5]|6[1-4])\d{3,4}\d{4}$|^0\d{2}\d{3,4}\d{4})$/
//targetValue.match(TEL_REGEXP) > undefined가 아니면 통과

const initialErrorInfo: ValidationErrorInfo = {
    isError: false,
    result: 'hidden',
    resultMsg: '',
}

export const useValidation = (props: UseValidationProps) => {
    let {
        type,
        value: targetValue,
        message,
        customFn,
        minLength,
        minLengthMsg,
        maxLength,
        maxLengthMsg,
        required,
        requiredMsg,
    } = props

    minLengthMsg = minLengthMsg ?? `최소 ${minLength}글자 이상로 입력해주세요`
    maxLengthMsg = minLengthMsg ?? `최대 ${maxLength}글자 이하로 입력해주세요`
    requiredMsg = requiredMsg ?? message ?? `값을 입력해 주세요`

    const [errorInfo, setErrorInfo] =
        React.useState<ValidationErrorInfo>(initialErrorInfo)

    const clear = () => {
        setErrorInfo(initialErrorInfo)
    }

    const [counter, setCounter] = React.useState(0)

    const validate = (iv?: string) => {
        setCounter((prev) => prev + 1)
        let isError = false
        let resultMessage = ''
        const tValue = iv ?? targetValue

        if (type === 'Custom' && customFn) {
            resultMessage = customFn?.(tValue)
        } else {
            if (type === 'Number') {
                if (isNaN(Number(tValue))) {
                    resultMessage = message
                }
            } else {
                if (tValue.length === 0) {
                    resultMessage = required ? requiredMsg : ''
                } else if (minLength && tValue.length < minLength) {
                    resultMessage = minLengthMsg
                } else if (maxLength && tValue.length > maxLength) {
                    resultMessage = maxLengthMsg
                } else {
                    if (type === 'String') {
                        if (!STRING_REGEXP.test(tValue)) {
                            resultMessage = message
                        }
                    } else if (type === 'Email') {
                        if (!comUtil.checkMail(tValue)) {
                            resultMessage = message
                        }
                    } else if (type === 'Korean') {
                        if (!KOREAN_REGEXP.test(tValue)) {
                            resultMessage = message
                        }
                    } else if (type === 'TEL') {
                        if (!tValue.match(TEL_REGEXP)) {
                            resultMessage = message
                        }
                    }
                }
            }
        }

        if (!_.isEmpty(resultMessage)) {
            isError = true
        }

        setErrorInfo({
            isError: isError,
            resultMsg: resultMessage,
            result: isError ? 'error' : 'hidden',
        })
    }

    const isBlured = counter > 0

    return {
        errorInfo: errorInfo,
        clearErrorInfo: clear,
        validate: validate,
        isBlured: isBlured,
    }
}
